import { formatDistanceToNow, format, isToday, isYesterday } from 'date-fns';

export function parseUTC(dateStr) {
  if (!dateStr) return null;
  // If already a Date object, return it
  if (dateStr instanceof Date) return dateStr;
  // Ensure it's a string before calling .includes
  const str = String(dateStr);
  if (str.includes('T') || str.includes('Z')) return new Date(str);
  return new Date(str.replace(' ', 'T') + 'Z');
}

export function fmtKg(v) {
  return v != null ? `${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 3 })} kg` : '—';
}

export function fmtCurrency(amt, cur = 'INR') {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(amt);
  } catch { return `${cur} ${Number(amt).toFixed(2)}`; }
}

export function formatTimeOnly(dateStr) {
  const d = parseUTC(dateStr);
  return d ? format(d, 'hh:mm a') : '—';
}

export function formatDate(dateStr) {
  const d = parseUTC(dateStr);
  return d ? format(d, 'd MMM yyyy, hh:mm a') : '—';
}

export function formatDateOnly(dateStr) {
  const d = parseUTC(dateStr);
  if (!d) return '—';
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'd MMM yyyy');
}

export function formatChatDate(dateStr) {
  const d = parseUTC(dateStr);
  if (!d) return '';
  if (isToday(d)) return format(d, 'hh:mm a');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'dd/MM/yyyy');
}

export function formatLastSeen(dateStr) {
  const d = parseUTC(dateStr);
  if (!d) return 'offline';
  try {
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'online';
    return 'last seen ' + formatDistanceToNow(d, { addSuffix: true });
  } catch { return 'offline'; }
}

export function formatRelative(dateStr) {
  const d = parseUTC(dateStr);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  }
  if (isToday(d)) return format(d, 'hh:mm a');
  if (isYesterday(d)) return 'Yesterday ' + format(d, 'hh:mm a');
  return format(d, 'd MMM yyyy');
}

export function groupMessagesByDate(messages) {
  const groups = [];
  let currentDateLabel = null;
  let currentGroup = null;
  for (const msg of messages) {
    const d = parseUTC(msg.created_at);
    if (!d) continue;
    const label = isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'MMMM d, yyyy');
    if (label !== currentDateLabel) {
      currentDateLabel = label;
      currentGroup = { date: label, messages: [] };
      groups.push(currentGroup);
    }
    currentGroup.messages.push(msg);
  }
  return groups;
}

export function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024)       return bytes + ' B';
  if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export function getInitials(name) {
  return (name || 'U').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

export function getAvatarColor(name) {
  const colors = ['#128C7E','#25D366','#075E54','#34B7F1','#ECE5DD','#FF6B35','#9B59B6','#E74C3C','#2ECC71','#3498DB'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

export function isImageFile(url) {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '');
}

export function isVideoFile(url) {
  return /\.(mp4|webm|ogg)$/i.test(url || '');
}

export function isAudioFile(url) {
  return /\.(mp3|ogg|wav|m4a)$/i.test(url || '');
}
